import { complianceCategoryLabel } from '@/features/bid-compliance/utils';
import { t } from '@/features/i18n/utils';
import { useAtom } from 'jotai';
import { createFileRoute, Link } from '@tanstack/react-router';
import z from 'zod';
import { BID_OVERVIEW_TABS } from '@/features/bids/constants';
import { COMPLIANCE_BUCKETS } from '@/features/bid-compliance/constants';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Icon } from '@/ui/components/icon';
import {
  AuctionIcon,
  LinkSquare02Icon,
  TextIndentIcon,
  CircleDotIcon,
} from '@hugeicons-pro/core-stroke-rounded';
import { Badge } from '@/ui/components/badge';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyMedia,
  EmptyTitle,
} from '@/ui/components/empty';
import { Button } from '@/ui/components/button';
import { H1 } from '@/ui/components/heading';
import { Source, SourceContent, SourceFileButton } from '@/ui/components/source';
import { ActionToolbarAskAIButton } from '@/ui/components/action-toolbar';
import { useInlineDocumentPreview } from '@/features/documents/preview/helpers';
import { Breadcrumb, BreadcrumbLink, BreadcrumbPage } from '@/ui/components/breadcrumb';
import {
  Header,
  HeaderBackButton,
  HeaderChatButton,
  HeaderNav,
  HeaderNextNavButton,
  HeaderPrevNavButton,
  useHeaderNav,
} from '@/ui/components/header';
import {
  MainContainer,
  MainHeader,
  MainHeaderBackButton,
  MainHeaderCopyButton,
  MainScrollArea,
} from '@/ui/components/main';
import { NotFound } from '@/ui/components/not-found';
import { ResizablePanel, ResizablePanelGroup } from '@/ui/components/resizable';
import { InlinePreviewPanel } from '@/features/documents/preview/components/inline-preview-panel';
import { BidBreadcrumbs } from '@/features/bids/components/bid-breadcrumbs';
import { DOCUMENT_TYPE } from '@/features/documents/constants';
import { CommentButton } from '@/features/comments/components/comment-button';
import { SubjectKind } from '@/subjects';
import { commentPanelOpenAtom } from '@/features/comments/store';
import { useGetCommentCounts } from '@/features/comments/queries';
import {
  CommentPanelContent,
  CommentPanelFollowToggle,
  CommentPanelHeader,
  CommentPanelInput,
  CommentPanelTitle,
  CommentPanelUsers,
  CommentPanelXClose,
  ResizableCommentPanel,
} from '@/features/comments/components/comment-panel';
import { bidDetailsQuery } from '@/features/bids/queries';
import { useProjectView } from '@/features/projects/helpers';
import type { BidComplianceCoverageSource } from '@/features/bid-compliance/types';
import { bidComplianceQuery } from '@/features/bid-compliance/queries';
import {
  useComplianceSection,
  useFilteredComplianceGroups,
} from '@/features/bid-compliance/helpers';
import { findingDisplayBucket, findingEvidence } from '@/features/bid-compliance/utils';
import {
  AISummaryAlert,
  AISummaryAlertAskAIButton,
  AISummaryAlertCopyButton,
  AISummaryAlertDescription,
  AISummaryAlertTitle,
} from '@/ui/components/ai-summary-alert';

const BUCKET_BADGE = {
  missing: 'tertiary-destructive',
  unclear: 'tertiary-warning',
  covered: 'tertiary-success',
} as const;

export const Route = createFileRoute(
  '/_authed/project/$projectId/workspaces/$workspaceId/bids/$bidId/_layout/compliance/$categoryId/$groupId',
)({
  staticData: { chatSurface: 'bid_compliance' },
  loader: ({ context, params }) =>
    context.queryClient.query({
      ...bidComplianceQuery(params.workspaceId, params.bidId),
      staleTime: 'static',
    }),
  component: RouteComponent,
  validateSearch: z.object({
    bucket: z.enum(COMPLIANCE_BUCKETS).optional().catch(undefined),
    tab: z.enum(BID_OVERVIEW_TABS).optional().catch(undefined),
  }),
});

function RouteComponent() {
  const view = useProjectView();
  const { projectId, workspaceId, bidId, categoryId, groupId } = Route.useParams();
  const search = Route.useSearch();
  const { data: bid } = useSuspenseQuery(bidDetailsQuery(bidId));
  const section = useComplianceSection();
  const filtered = useFilteredComplianceGroups();
  const openPreview = useInlineDocumentPreview();

  const [commentPanelOpen, setCommentPanelOpen] = useAtom(commentPanelOpenAtom);
  const { countsMap: commentCounts } = useGetCommentCounts({
    type: DOCUMENT_TYPE.bid,
    resourceId: bidId,
  });

  const listLink = { to: '..', search: { bucket: search.bucket, tab: search.tab } } as const;
  const group = section?.groups.find((g) => g.id === groupId);
  const { handlePrevious, handleNext, isPreviousDisabled, isNextDisabled } = useHeaderNav(
    group ? filtered[findingDisplayBucket(group)] : [],
    groupId,
    (id) => ({
      to: '/project/$projectId/workspaces/$workspaceId/bids/$bidId/compliance/$categoryId/$groupId',
      params: { projectId, workspaceId, bidId, categoryId, groupId: id },
      search: { bucket: search.bucket, tab: search.tab },
    }),
  );
  if (!section || !group) return <NotFound />;

  const bucket = findingDisplayBucket(group);
  const number = group.displayNumber;
  const categoryLabel = complianceCategoryLabel(section.category, view);
  const commentEntityId = `${categoryId}:${group.groupKey}`;
  const counts = commentCounts?.get(`${SubjectKind.BID_SUBMISSION_COVERAGE}:${commentEntityId}`);

  const evidence = findingEvidence(group);
  const previewSource = (source: BidComplianceCoverageSource, title: string | null) =>
    openPreview({
      documentId: source.sourceDocId,
      title,
      source: source.sourceSpan,
      content: source.sourceSpan,
      pageStart: source.page,
    });

  return (
    <>
      <Header>
        <HeaderBackButton {...listLink} />
        <BidBreadcrumbs>
          <BreadcrumbLink
            to="/project/$projectId/workspaces/$workspaceId/bids/$bidId"
            params={{ projectId, workspaceId, bidId }}
          >
            {bid.bidType === 'PREQUALIFICATION'
              ? t({ en: 'Qualification checklist', nb: 'Kvalifiseringssjekkliste' })
              : t({ en: 'Bid compliance checklist', nb: 'Kravsjekk' })}
          </BreadcrumbLink>
          <BreadcrumbLink {...listLink}>{categoryLabel}</BreadcrumbLink>
          <BreadcrumbPage className="tabular-nums">#{number}</BreadcrumbPage>
        </BidBreadcrumbs>
        <HeaderNav>
          <HeaderPrevNavButton onClick={handlePrevious} disabled={isPreviousDisabled} />
          <HeaderNextNavButton onClick={handleNext} disabled={isNextDisabled} />
        </HeaderNav>
        <HeaderChatButton />
      </Header>
      <ResizablePanelGroup>
        <MainScrollArea render={<ResizablePanel minSize="200px" className="@container" />}>
          <MainHeader className="border-b flex-nowrap">
            <H1>
              <MainHeaderBackButton {...listLink} />
              <span className="line-clamp-1">{group.displayTitle}</span>
            </H1>
            <div className="flex items-center gap-0.5">
              <MainHeaderCopyButton />
              <CommentButton
                totalCount={counts?.totalCount ?? 0}
                unreadCount={counts?.unreadCount ?? 0}
                data-selected={commentPanelOpen ? '' : undefined}
                onClick={() => setCommentPanelOpen((prev) => !prev)}
              />
            </div>
          </MainHeader>
          <MainContainer>
            {group.summary ? (
              <AISummaryAlert text={group.summary}>
                <AISummaryAlertTitle>
                  <AISummaryAlertCopyButton />
                  <AISummaryAlertAskAIButton />
                </AISummaryAlertTitle>
                <AISummaryAlertDescription />
              </AISummaryAlert>
            ) : null}
            <section className="mt-5">
              <p className="text-muted text-sm mb-3">
                {t({ en: 'Evidence / sources', nb: 'Evidens / kilder' })}:
              </p>
              <div className="grid @2xl:grid-cols-2 gap-5">
                <div className="space-y-3">
                  <div className="flex items-center gap-1.5">
                    <Badge variant="tertiary" size="sm" className="text-sm">
                      <Icon icon={CircleDotIcon} className="size-3.5" />
                      {t({ en: 'What the tender requires', nb: 'Hva anbudet krever' })}
                    </Badge>
                    {evidence.member?.notRelevant ? (
                      <Badge variant="tertiary" size="sm" className="text-sm text-muted">
                        {t({ en: 'Not relevant', nb: 'Ikke relevant' })}
                      </Badge>
                    ) : null}
                  </div>
                  <Source
                    text={evidence.member?.tenderDescription ?? group.displayTitle}
                    className="ml-0.5"
                  >
                    <SourceContent format={(text) => `'...${text}'`}>
                      <ActionToolbarAskAIButton />
                    </SourceContent>
                    {evidence.tenderSource?.sourceDocId ? (
                      <SourceFileButton
                        fileName={evidence.sourceName ?? ''}
                        onClick={() => previewSource(evidence.tenderSource!, evidence.sourceName)}
                      />
                    ) : null}
                  </Source>
                </div>
                <div className="space-y-3">
                  <Badge
                    variant={BUCKET_BADGE[bucket]}
                    size="sm"
                    className="text-foreground text-sm"
                  >
                    <Icon icon={CircleDotIcon} className="size-3.5" />
                    {t({
                      en:
                        view === 'client'
                          ? 'What your submission says today'
                          : 'What your bid says today',
                      nb:
                        view === 'client'
                          ? 'Hva innleveringen sier i dag'
                          : 'Hva tilbudet sier i dag',
                    })}
                  </Badge>
                  {evidence.bidQuote ? (
                    <Source text={evidence.bidQuote} className="ml-0.5">
                      <SourceContent format={(text) => `'...${text}'`}>
                        <ActionToolbarAskAIButton />
                      </SourceContent>
                      {evidence.bidSource?.sourceDocId ? (
                        <SourceFileButton
                          fileName={evidence.bidSource.sourceFilename ?? ''}
                          onClick={() =>
                            previewSource(evidence.bidSource!, evidence.bidSource!.sourceFilename)
                          }
                        />
                      ) : null}
                    </Source>
                  ) : (
                    <Empty
                      data-missing={bucket === 'missing' ? '' : undefined}
                      className="rounded-md relative max-w-full w-full border text-center border-dashed p-5 pb-4 data-missing:border-red-6 data-missing:bg-red-2"
                    >
                      <EmptyMedia variant="icon">
                        <Icon icon={bucket === 'missing' ? AuctionIcon : TextIndentIcon} />
                      </EmptyMedia>
                      <EmptyContent className="gap-1">
                        <EmptyTitle className="text-base font-medium">
                          {bucket === 'missing'
                            ? t({
                                en:
                                  view === 'client'
                                    ? 'No mention in submission'
                                    : 'No mention in bid',
                                nb:
                                  view === 'client'
                                    ? 'Ingen omtale i innleveringen'
                                    : 'Ingen omtale i tilbudet',
                              })
                            : t({ en: 'No direct quote found', nb: 'Fant ingen direkte sitat' })}
                        </EmptyTitle>
                        <EmptyDescription className="text-sm text-muted">
                          {bucket === 'missing'
                            ? t({
                                en:
                                  view === 'client'
                                    ? 'No submission content that matches this requirement'
                                    : 'No bid content that matches this requirement',
                                nb:
                                  view === 'client'
                                    ? 'Ingen innleveringsinnhold som samsvarer med dette kravet'
                                    : 'Ingen tilbudsinnhold som samsvarer med dette kravet',
                              })
                            : t({
                                en:
                                  view === 'client'
                                    ? 'No exact passage from the submission could be linked to this requirement — see reasoning'
                                    : 'No exact passage from the bid could be linked to this requirement — see reasoning',
                                nb:
                                  view === 'client'
                                    ? 'Fant ingen eksakt tekst i innleveringen som kan knyttes til dette kravet — se begrunnelse'
                                    : 'Fant ingen eksakt tekst i tilbudet som kan knyttes til dette kravet — se begrunnelse',
                              })}
                        </EmptyDescription>
                        <Button
                          variant="secondary"
                          kind="with-icon"
                          size="sm"
                          nativeButton={false}
                          render={
                            <Link
                              to="/project/$projectId/workspaces/$workspaceId/bids/$bidId/files"
                              params={{ projectId, workspaceId, bidId }}
                            />
                          }
                        >
                          {t({
                            en: view === 'client' ? 'Go to submission files' : 'Go to bid files',
                            nb:
                              view === 'client'
                                ? 'Gå til innleveringsfiler'
                                : 'Gå til tilbudsfiler',
                          })}
                          <Icon icon={LinkSquare02Icon} />
                        </Button>
                      </EmptyContent>
                    </Empty>
                  )}
                </div>
              </div>
            </section>
          </MainContainer>
        </MainScrollArea>
        <InlinePreviewPanel />
        <ResizableCommentPanel
          key={commentEntityId}
          open={commentPanelOpen}
          onOpenChange={setCommentPanelOpen}
          className="border-l"
          type={DOCUMENT_TYPE.bid}
          resourceId={bidId}
          subjectKind={SubjectKind.BID_SUBMISSION_COVERAGE}
          subjectId={commentEntityId}
        >
          <CommentPanelHeader>
            <CommentPanelTitle>
              <Breadcrumb>
                <BreadcrumbPage>
                  <span className="line-clamp-1">{categoryLabel}</span>
                </BreadcrumbPage>
                <BreadcrumbPage className="tabular-nums">#{number}</BreadcrumbPage>
              </Breadcrumb>
            </CommentPanelTitle>
            <CommentPanelFollowToggle />
            <CommentPanelUsers />
            <CommentPanelXClose onClick={() => setCommentPanelOpen(false)} />
          </CommentPanelHeader>
          <CommentPanelContent />
          <CommentPanelInput className="p-3 pt-0" />
        </ResizableCommentPanel>
      </ResizablePanelGroup>
    </>
  );
}
